import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Check, Mail, Calendar, MapPin, User, ArrowRight, Clock, AlertCircle } from 'lucide-react';
import BookingResultView, { type BookingResultResponse } from '@/components/BookingResultView';

const PAYPAL_CAPTURE_WEBHOOK = 'https://n8n.cogniiq.co/webhook/paypal-return';

function formatCourt(court: string): string {
  if (!court) return '';
  const parts = court.split('-');
  if (parts.length === 0) return court;
  const first = parts[0].charAt(0).toUpperCase() + parts[0].slice(1);
  return parts.length === 1 ? first : `${first} ${parts.slice(1).join(' ')}`;
}

function formatDateTime(iso: string): { date: string; time: string } {
  if (!iso) return { date: '', time: '' };
  try {
    const d = new Date(iso);
    const date = new Intl.DateTimeFormat('de-DE', {
      weekday: 'long', day: '2-digit', month: 'long', year: 'numeric',
      timeZone: 'Europe/Berlin',
    }).format(d);
    const time = new Intl.DateTimeFormat('de-DE', {
      hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Berlin',
    }).format(d) + ' Uhr';
    return { date, time };
  } catch {
    return { date: iso, time: '' };
  }
}

const CONFETTI = Array.from({ length: 48 }, (_, i) => ({
  id: i,
  x: Math.random() * 100,
  delay: Math.random() * 2.5,
  duration: 3 + Math.random() * 2.5,
  size: 5 + Math.random() * 5,
  color: ['#C8311A', '#e85c42', '#f9ddd9', '#d1d5db', '#C8311A', '#fff'][Math.floor(Math.random() * 6)],
  rotate: Math.random() * 360,
  shape: Math.random() > 0.5 ? 'rounded-sm' : 'rounded-full',
}));

interface SuccessPageProps {
  onNavigate?: (page: string) => void;
}

export default function SuccessPage({ onNavigate }: SuccessPageProps) {
  const p = new URLSearchParams(window.location.search);
  const court = p.get('court') ?? '';
  const start = p.get('start') ?? '';
  const name = p.get('name') ?? '';
  const amount = p.get('amount') ?? '';
  const paypalToken = p.get('token') ?? '';

  const [paypalState, setPaypalState] = useState<'idle' | 'capturing' | 'done' | 'error' | 'conflict'>(
    paypalToken ? 'capturing' : 'idle'
  );
  const [conflictResult, setConflictResult] = useState<BookingResultResponse | null>(null);
  const [paypalError, setPaypalError] = useState('');

  useEffect(() => {
    if (!paypalToken) return;

    const key = `paypal_booking_processed_${paypalToken}`;
    if (sessionStorage.getItem(key)) {
      setPaypalState('done');
      return;
    }

    (async () => {
      try {
        const res = await fetch(PAYPAL_CAPTURE_WEBHOOK, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ orderID: paypalToken }),
        });

        if (!res.ok) {
          let json: Partial<BookingResultResponse> | null = null;
          try {
            const ct = res.headers.get('content-type') ?? '';
            if (ct.includes('application/json')) {
              json = await res.json();
            } else {
              const text = await res.text().catch(() => '');
              try { json = JSON.parse(text); } catch { /* not json */ }
            }
          } catch { /* ignore */ }

          if (json?.success === false || res.status === 409) {
            setConflictResult({
              success: false,
              bookingConfirmed: false,
              paymentRefunded: json?.paymentRefunded ?? true,
              title: json?.title || 'Die Buchung konnte nicht abgeschlossen werden',
              message: json?.message || 'Falls eine Zahlung erfolgt ist, prüfen wir sie automatisch.',
              refundMessage: json?.refundMessage,
              nextStep: json?.nextStep,
              contact: json?.contact,
            });
            setPaypalState('conflict');
          } else {
            setPaypalError(`Fehler ${res.status}`);
            setPaypalState('error');
          }
          return;
        }

        sessionStorage.setItem(key, '1');
        setPaypalState('done');
      } catch (e) {
        console.error('[PayPal Capture]', e);
        setPaypalError(e instanceof Error ? e.message : 'Unbekannter Fehler');
        setPaypalState('error');
      }
    })();
  }, [paypalToken]);

  const formattedCourt = formatCourt(court);
  const { date: formattedDate, time: formattedTime } = formatDateTime(start);
  const hasDetails = formattedCourt || formattedDate || name;

  const [confetti] = useState(() => CONFETTI);

  // While capturing PayPal payment, show a minimal loading screen
  if (paypalState === 'capturing') {
    return (
      <div className="min-h-screen svh-canvas flex flex-col items-center justify-center px-4 py-16">
        <div className="w-full max-w-[560px]">
          <div className="text-center mb-8">
            <img
              src="/photos/Image.jpeg"
              alt="SV Heinersreuth"
              className="h-14 w-14 rounded-full object-cover mx-auto mb-3 shadow-sm ring-1 ring-black/5"
            />
            <p className="text-[15px] font-bold tracking-tight" style={{ color: 'var(--svh-ink)' }}>
              SV Heinersreuth
            </p>
            <p className="svh-label mt-1">Padel &amp; Tennis</p>
          </div>

          <div className="svh-card svh-card-lg svh-card-raised overflow-hidden">
            <div className="h-1 w-full" style={{ background: 'var(--svh-crest)' }} />
            <div className="p-8 md:p-10">
              <div className="flex flex-col items-center justify-center py-6 gap-5 text-center">
                <div
                  className="w-14 h-14 rounded-full flex items-center justify-center"
                  style={{ background: 'var(--svh-crest-wash)' }}
                >
                  <div
                    className="w-7 h-7 rounded-full animate-spin"
                    style={{ border: '2px solid var(--svh-crest-wash)', borderTopColor: 'var(--svh-crest)' }}
                  />
                </div>
                <div>
                  <p className="svh-h3 mb-1.5">Buchung wird abgeschlossen …</p>
                  <p className="svh-meta">Einen Moment bitte. Bitte schließen Sie dieses Fenster nicht.</p>
                </div>
              </div>
            </div>
          </div>

          <p className="svh-meta text-center mt-6">SV Heinersreuth e.V.</p>
        </div>
      </div>
    );
  }

  if (paypalState === 'conflict') {
    return (
      <BookingResultView
        result={conflictResult}
        autoRedirect={false}
        redirectUrl="https://svheinersreuth-padel.de/"
      />
    );
  }

  if (paypalState === 'error') {
    return (
      <div className="min-h-screen svh-canvas flex flex-col items-center justify-center px-4 py-16">
        <div className="w-full max-w-[560px]">
          <div className="text-center mb-8">
            <img
              src="/photos/Image.jpeg"
              alt="SV Heinersreuth"
              className="h-14 w-14 rounded-full object-cover mx-auto mb-3 shadow-sm ring-1 ring-black/5"
            />
            <p className="text-[15px] font-bold tracking-tight" style={{ color: 'var(--svh-ink)' }}>
              SV Heinersreuth
            </p>
            <p className="svh-label mt-1">Padel &amp; Tennis</p>
          </div>

          <div className="svh-card svh-card-lg svh-card-raised overflow-hidden">
            <div className="h-1 w-full" style={{ background: 'var(--svh-crest)' }} />
            <div className="p-8 md:p-10">
              <div className="flex items-center justify-center mb-6">
                <div className="w-16 h-16 rounded-full bg-red-50 flex items-center justify-center">
                  <AlertCircle className="w-8 h-8 text-red-600" strokeWidth={1.75} />
                </div>
              </div>
              <p className="text-[12px] font-bold tracking-[0.16em] uppercase text-red-700 mb-3 text-center">
                Buchung fehlgeschlagen
              </p>
              <h1 className="svh-h2 text-center mb-4">Buchung konnte nicht abgeschlossen werden</h1>
              <p className="svh-body text-center mb-6">{paypalError}</p>

              <hr className="svh-rule mb-6" />

              <a href="/" className="svh-btn svh-btn--primary svh-focus w-full">
                Zur Startseite
                <ArrowRight className="w-4 h-4" />
              </a>
            </div>
          </div>

          <p className="svh-meta text-center mt-6">SV Heinersreuth e.V.</p>
        </div>
      </div>
    );
  }

  const detailRows = [
    name && { icon: User, label: 'Name', value: name },
    formattedCourt && { icon: MapPin, label: 'Platz', value: formattedCourt },
    formattedDate && { icon: Calendar, label: 'Datum', value: formattedDate },
    formattedTime && { icon: Clock, label: 'Uhrzeit', value: formattedTime },
  ].filter(Boolean) as { icon: React.ElementType; label: string; value: string }[];

  return (
    <div className="min-h-screen svh-canvas flex flex-col items-center justify-center px-4 py-16 relative overflow-hidden">

      {/* Restrained confetti (existing celebratory motion, softened) */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden opacity-40">
        {confetti.map((c) => (
          <motion.div
            key={c.id}
            className={`absolute ${c.shape}`}
            style={{
              left: `${c.x}%`,
              top: '-20px',
              width: c.size,
              height: c.size * 0.45,
              backgroundColor: c.color,
            }}
            initial={{ y: -20, rotate: c.rotate, opacity: 0 }}
            animate={{
              y: ['0vh', '110vh'],
              rotate: [c.rotate, c.rotate + 540 * (Math.random() > 0.5 ? 1 : -1)],
              opacity: [0, 0.75, 0.75, 0],
            }}
            transition={{
              duration: c.duration,
              delay: c.delay,
              ease: 'linear',
              repeat: Infinity,
              repeatDelay: 3 + Math.random() * 4,
            }}
          />
        ))}
      </div>

      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
        className="relative z-10 w-full max-w-[560px]"
      >
          <div className="text-center mb-8">
            <img
              src="/photos/Image.jpeg"
              alt="SV Heinersreuth"
              className="h-14 w-14 rounded-full object-cover mx-auto mb-3 shadow-sm ring-1 ring-black/5"
            />
            <p className="text-[15px] font-bold tracking-tight" style={{ color: 'var(--svh-ink)' }}>
              SV Heinersreuth
            </p>
            <p className="svh-label mt-1">Padel &amp; Tennis</p>
          </div>

        <motion.div
          initial={{ opacity: 0, scale: 0.97 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.3, delay: 0.1 }}
          className="svh-card svh-card-lg svh-card-raised overflow-hidden"
        >
          <div className="h-1 w-full" style={{ background: 'var(--svh-crest)' }} />
          <div className="p-8 md:p-10">

            {/* Success icon */}
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.4, delay: 0.2 }}
              className="flex items-center justify-center mb-6"
            >
              <div className="w-16 h-16 rounded-full bg-emerald-50 flex items-center justify-center">
                <Check className="w-8 h-8 text-emerald-600" strokeWidth={2.5} />
              </div>
            </motion.div>

            {/* Headline */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.25 }}
              className="text-center mb-8"
            >
              <p className="text-[12px] font-bold tracking-[0.16em] uppercase text-emerald-700 mb-3">
                Buchung bestätigt
              </p>
              <h1 className="svh-h2 mb-4">Vielen Dank!</h1>
              <p className="svh-body">
                Ihre Buchung ist eingegangen und wird bearbeitet.
              </p>
            </motion.div>

            {/* Booking details */}
            {hasDetails && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: 0.3 }}
                className="rounded-[10px] overflow-hidden mb-4"
                style={{ background: 'var(--svh-surface-sub)', border: '1px solid var(--svh-border)' }}
              >
                <div className="px-5 pt-4 pb-2">
                  <p className="svh-label">Buchungsdetails</p>
                </div>
                <div className="px-5 pb-4 space-y-3">
                  {detailRows.map(({ icon: Icon, label, value }) => (
                    <div key={label} className="flex items-start gap-3">
                      <Icon className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: 'var(--svh-ink-3)' }} strokeWidth={2} />
                      <div className="min-w-0">
                        <p className="svh-meta mb-0.5">{label}</p>
                        <p className="text-[14px] font-semibold break-words" style={{ color: 'var(--svh-ink)' }}>{value}</p>
                      </div>
                    </div>
                  ))}
                  {amount && (
                    <>
                      <hr className="svh-rule" />
                      <div className="flex items-center justify-between">
                        <span className="svh-label">Bezahlter Betrag</span>
                        <span className="text-[16px] font-bold svh-num" style={{ color: 'var(--svh-ink)' }}>
                          {amount} €
                        </span>
                      </div>
                    </>
                  )}
                </div>
              </motion.div>
            )}

            {/* Email notice */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.35 }}
              className="rounded-[10px] overflow-hidden mb-6"
              style={{ border: '1px solid var(--svh-border)' }}
            >
              <div className="flex items-start gap-3 px-5 py-4 bg-emerald-50/60">
                <Mail className="w-4 h-4 text-emerald-700 flex-shrink-0 mt-0.5" strokeWidth={2} />
                <p className="text-[13px] leading-relaxed" style={{ color: 'var(--svh-ink-2)' }}>
                  Die Bestätigungs-E-Mail{name ? <>, <span className="font-semibold" style={{ color: 'var(--svh-ink)' }}>{name}</span>,</> : ''} trifft in Kürze in Ihrem Postfach ein.
                </p>
              </div>
              <div className="flex items-start gap-3 px-5 py-4 bg-amber-50/70 border-t" style={{ borderColor: 'var(--svh-border)' }}>
                <span className="text-[15px] flex-shrink-0" role="img" aria-label="Spam">📬</span>
                <p className="text-[13px] leading-relaxed text-amber-900">
                  <span className="font-semibold">Keine E-Mail erhalten?</span> Bitte überprüfen Sie auch Ihren <span className="font-semibold">Spam- oder Junk-Ordner</span> — manchmal landet unsere Mail dort.
                </p>
              </div>
            </motion.div>

            <hr className="svh-rule mb-6" />

            {/* Actions */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.4 }}
              className="space-y-3"
            >
              <button
                onClick={() => (window.location.href = '/')}
                className="svh-btn svh-btn--primary svh-focus w-full"
              >
                Zur Startseite
                <ArrowRight className="w-4 h-4" />
              </button>

              <button
                onClick={() => (window.location.href = '/?page=booking')}
                className="svh-btn svh-btn--secondary svh-focus w-full"
              >
                Weitere Buchung vornehmen
              </button>
            </motion.div>
          </div>
        </motion.div>

        {/* Support link */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.3, delay: 0.5 }}
          className="text-center mt-6"
        >
          <p className="svh-meta">Fragen?</p>
          <a
            href="/?page=contact"
            onClick={e => { if (onNavigate) { e.preventDefault(); onNavigate('contact'); } }}
            className="svh-focus inline-flex items-center justify-center min-h-[44px] px-4 text-[13px] font-semibold underline underline-offset-4 cursor-pointer"
            style={{ color: 'var(--svh-crest)' }}
          >
            Kontakt aufnehmen
          </a>
        </motion.div>

        <p className="svh-meta text-center mt-3">SV Heinersreuth e.V.</p>
      </motion.div>
    </div>
  );
}
